import React from 'react';
import classnames from 'classnames';
import type { PrimaryPlacement } from '../../types';
import s from './PopoverArrow.scss';

const arrowSize = parseInt(s['arrow_size']);

const getArrowPosition = (
  placement: PrimaryPlacement,
  position: number,
  containerSize: number
) => {
  const highLimit = containerSize - arrowSize * 2;
  const lowLimit = arrowSize;
  // The offset adjusts the arrow to point correctly to the reference.
  // We need this because our arrow is not centered by design.
  const offset = ['top', 'left'].includes(placement)
    ? arrowSize / 2
    : -arrowSize / 2;

  return Math.max(lowLimit, Math.min(highLimit, position + offset));
};

const getArrowPositionStyles = (
  style: React.CSSProperties,
  placement: PrimaryPlacement,
  contentWidth?: number,
  contentHeight?: number
): React.CSSProperties => {
  let { left, top } = style;

  if (Number.isInteger(left) && contentWidth) {
    left = getArrowPosition(placement, left as number, contentWidth);
  } else if (Number.isInteger(top) && contentHeight) {
    top = getArrowPosition(placement, top as number, contentHeight);
  }

  return { ...style, left, top };
};

export interface Props {
  placement: PrimaryPlacement;
  contentWidth?: number;
  contentHeight?: number;
  style?: React.CSSProperties;
  /** ONLY for popper use, do not piggyback */
  attributes?: { [key: string]: string };
  className?: string;
  wrapperClassName?: string;
  /** when using a custom arrow svg, turn off the hacks we do for the defaults */
  simpleArrowPosition?: boolean;
}

/** Basic popover arrow component, does not apply any styles, only positioning.
 *  You will need to apply your own arrow svg etc. when using this
 */
const PopoverArrow = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      className,
      wrapperClassName,
      style = {},
      simpleArrowPosition,
      placement,
      contentWidth,
      contentHeight,
      attributes,
    },
    ref
  ) => {
    return (
      <div
        {...attributes}
        ref={ref}
        className={classnames(s['root'], s[placement], wrapperClassName)}
        style={
          simpleArrowPosition
            ? style
            : getArrowPositionStyles(
                style,
                placement,
                contentWidth,
                contentHeight
              )
        }
      >
        <div className={classnames(s['arrow'], className)} />
      </div>
    );
  }
);
PopoverArrow.displayName = 'PopoverArrow';

export default PopoverArrow;
